/**
 * CM Betfair Balance Dashboard —
 * `POST /v1/book/betfair-market-event-exposure` (Lab Sportsbook BetfairMarketExposure).
 *
 * Vite DEV only: if CM-API returns 404 (route not deployed yet), fall back to
 * Phase-1 fixture rows. Production / UAT builds never use fixtures.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  cmApi,
  type BetfairMarketEventExposureRequest,
  type BetfairMarketEventExposureResponse,
  type BetfairMarketEventExposureRow,
} from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import {
  queryBalanceRows,
  type BalancePageSize,
  type BalanceSport,
  type BetfairBalancePage,
  type BetfairBalanceQuery,
  type BetfairBalanceRow,
} from '@/features/betfair-balance/domain'
import { FIXTURE_BALANCE_ROWS } from '@/features/betfair-balance/fixture'

/** Lab silent refresh interval. */
export const BF_BALANCE_POLL_MS = 5_000

export type BetfairBalanceSource = 'live' | 'fixture'

export type BetfairBalanceQueryResult = BetfairBalancePage & {
  source: BetfairBalanceSource
}

export function buildBalanceRequest(
  query: BetfairBalanceQuery,
): BetfairMarketEventExposureRequest {
  return {
    page: query.page,
    maxRow: query.maxRow,
    ...(query.sport !== 'ALL' ? { sport: query.sport } : {}),
  }
}

export function toBalanceRow(row: BetfairMarketEventExposureRow): BetfairBalanceRow {
  return {
    day: row.day ?? '',
    username: row.username ?? '',
    eventtype: row.eventtype ?? '',
    event: row.event ?? '',
    eventid: String(row.eventid ?? ''),
    betfair_bets: Number(row.betfair_bets) || 0,
    betfair_stake_used: Number(row.betfair_stake_used) || 0,
    net_exposure: Number(row.net_exposure) || 0,
  }
}

export function toBalancePage(
  res: BetfairMarketEventExposureResponse,
  query: BetfairBalanceQuery,
): BetfairBalancePage {
  return {
    results: (res?.results ?? []).map(toBalanceRow),
    count: res?.count ?? 0,
    page: res?.page ?? query.page,
    maxRow: (res?.maxRow ?? query.maxRow) as BalancePageSize,
  }
}

/** True when CM-API has not been deployed with this route yet (404 only). */
export function isBalanceEndpointMissing(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '')
  const lower = msg.toLowerCase()
  return (
    lower.includes('not found') ||
    lower.includes('cannot post') ||
    lower.includes('404')
  )
}

export function useBetfairBalanceDashboard(query: {
  sport: BalanceSport
  page: number
  maxRow: BalancePageSize
  enabled?: boolean
}) {
  const enabled = query.enabled ?? true
  const balanceQuery: BetfairBalanceQuery = {
    sport: query.sport,
    page: query.page,
    maxRow: query.maxRow,
  }

  return useQuery({
    queryKey: [
      'cm',
      'betfair-balance',
      query.sport,
      query.page,
      query.maxRow,
    ],
    queryFn: async (): Promise<BetfairBalanceQueryResult> => {
      try {
        const res = await cmApi.betfairMarketEventExposure(
          authHeaders(),
          buildBalanceRequest(balanceQuery),
        )
        return { ...toBalancePage(res, balanceQuery), source: 'live' }
      } catch (err) {
        // Local Vite only — never mask 404 with fixtures in deployed builds.
        if (import.meta.env.DEV && isBalanceEndpointMissing(err)) {
          return {
            ...queryBalanceRows(FIXTURE_BALANCE_ROWS, balanceQuery),
            source: 'fixture',
          }
        }
        throw err
      }
    },
    enabled,
    staleTime: BF_BALANCE_POLL_MS,
    refetchInterval: (q) => {
      if (!enabled) return false
      // Stop hammering UAT once we know the route is undeployed.
      if (q.state.data?.source === 'fixture') return false
      return BF_BALANCE_POLL_MS
    },
    placeholderData: keepPreviousData,
  })
}
